import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { MenuItem, MENU_ITEMS } from '../../constants/menu-item';
import { SidenavService } from 'src/app/core/services/sidenav.service';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from 'src/app/core/services/auth.service';
import { CurrentUserCompanyInfo } from '../../classes/current-user-company-info';
import { BaseForm } from '../../classes/base-form';
import { mixOf } from '../../classes/mixin-builder';
import { NavigationEnd, Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { SharedModule } from '../../shared.module';
import { ToolbarComponent } from '../toolbar/toolbar.component';

@Component({
	selector: 'app-sidenav',
	templateUrl: './sidenav.component.html',
	styleUrls: ['./sidenav.component.scss'],
	standalone: true,
	imports: [SharedModule, LucideAngularModule, ToolbarComponent],
	encapsulation: ViewEncapsulation.None
})
export class SidenavComponent
	extends mixOf(BaseForm).with(CurrentUserCompanyInfo)
	implements OnInit
{
	menuItems: MenuItem[] = [];

	breakpointSidenavState = false;
	userSidenavState = false;
	validarVisualizadorProduccion: boolean;
	drawerSize = { xSmall: true, small: false, medium: false, large: false };

	currentUserInfo: any;
	userCompanies: { [key: string]: any } = {};

	constructor(
		public spinnerService: SpinnerService,
		private readonly router: Router,
		private readonly responsive: BreakpointObserver,
		private readonly sidenavService: SidenavService,
		private readonly msalService: MsalService,
		private readonly authService: AuthService
	) {
		super();
		this.isMenuActive();
		this.getSystemOperator();
	}

	ngOnInit(): void {
		this.handleUserSidenavInteraction();
		this.handleXSmall();
		this.handleSmall();
		this.handleMedium();
		this.handleLarge();
		this.getUserInfo();
		this.getSystemOperator();
		this.setCurrentUserCompanyInfo();
		this.getUserCompany();

		this.updateMenuVisibility();
		this.authService.userRoles.subscribe(() => this.updateMenuVisibility());
	}

	private updateMenuVisibility(): void {
		this.menuItems = MENU_ITEMS.map(menuItem => ({
			...menuItem,
			visible: this.isMenuItemVisible(menuItem),
			subMenu: menuItem.subMenu.map(subMenu => ({
				...subMenu,
				visible: this.isSubMenuVisible(subMenu)
			}))
		}));
	}

	private isMenuItemVisible(menuItem: MenuItem): boolean {
		return menuItem.subMenu.some(subMenu => this.isSubMenuVisible(subMenu));
	}

	private isSubMenuVisible(subMenu: any): boolean {
		if (subMenu.enabled === false) return false;
		if (!subMenu.requiredPermission) {
			return true;
		}

		return this.authService.hasPermission(subMenu.requiredPermission);
	}

	getUserInfo() {
		this.currentUserInfo = this.authService.getUserName();
	}

	logoff() {
		sessionStorage.clear();
		this.msalService.logout();
	}

	seleccionarEmpresa(empresaId: number) {
		if (empresaId) {
			this.authService.changeCompany(empresaId);
		}
	}

	getUserCompany() {
		this.authService.userCompanies.subscribe(userCompanies => {
			for (const datosEmpresa of userCompanies) {
				const nombreEmpresa = datosEmpresa.razonSocial;
				const claveUnica = `${nombreEmpresa}`;
				this.userCompanies[claveUnica] = datosEmpresa.id;
			}
		});
	}

	private isMenuActive() {
		this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.menuItems
					.filter(item => item.routeMenu != '')
					.forEach(menuItem => {
						menuItem.active = event.urlAfterRedirects.includes(
							menuItem.routeMenu
						);
					});
			}
		});
	}

	handleUserSidenavInteraction(): void {
		this.sidenavService.sidenavState.subscribe(state => {
			this.userSidenavState = state.opened;
		});
	}

	handleXSmall(): void {
		this.responsive.observe(Breakpoints.XSmall).subscribe(breakpoint => {
			if (breakpoint.matches) {
				this.breakpointSidenavState = false;
				this.drawerSize.xSmall = true;
				this.drawerSize.small = false;
				this.drawerSize.medium = false;
				this.drawerSize.large = false;
			}
		});
	}

	handleSmall(): void {
		this.responsive.observe(Breakpoints.Small).subscribe(breakpoint => {
			if (breakpoint.matches) {
				this.breakpointSidenavState = false;
				this.drawerSize.xSmall = false;
				this.drawerSize.small = true;
				this.drawerSize.medium = false;
				this.drawerSize.large = false;
			}
		});
	}

	setActiveMenuItem(i: number, ruta: string) {
		if (!this.router.url.split('/').includes(ruta)) {
			this.menuItems[i].active = false;
		}
	}

	handleMedium(): void {
		this.responsive.observe(Breakpoints.Medium).subscribe(breakpoint => {
			if (breakpoint.matches) {
				this.breakpointSidenavState = true;
				this.drawerSize.xSmall = false;
				this.drawerSize.small = false;
				this.drawerSize.medium = true;
				this.drawerSize.large = false;
				this.close();
			}
		});
	}

	handleLarge(): void {
		this.responsive.observe(Breakpoints.WebLandscape).subscribe(breakpoint => {
			if (breakpoint.matches) {
				this.breakpointSidenavState = true;
				this.drawerSize.xSmall = false;
				this.drawerSize.small = false;
				this.drawerSize.medium = false;
				this.drawerSize.large = true;
				this.close();
			}
		});
	}

	close(): void {
		this.sidenavService.hide();
	}
}
